/*
 * ST 2026-10-08 (Wrike 4580728412, step 2): inline discount code card in the cart drawer.
 * Markup: snippets/st-cart-discount.liquid. Uses the Ajax Cart API (`/cart/update.js` with `discount`)
 * and the theme's cartUpdate pub/sub, so the drawer re-renders like after any other cart change.
 * Applied codes are rendered by Liquid from `cart.discount_applications`, so they survive a reload.
 */
if (!customElements.get('st-cart-discount')) {
  customElements.define(
    'st-cart-discount',
    class StCartDiscount extends HTMLElement {
      connectedCallback() {
        this.form = this.querySelector('form');
        this.input = this.querySelector('input[name="discount"]');
        this.apply = this.querySelector('.st-cart-discount__apply');
        this.error = this.querySelector('.st-cart-discount__error');

        this.onClick = this.onClick.bind(this);
        this.onSubmit = this.onSubmit.bind(this);
        this.onInput = this.onInput.bind(this);
        this.addEventListener('click', this.onClick);
        this.form?.addEventListener('submit', this.onSubmit);
        this.input?.addEventListener('input', this.onInput);
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onClick);
        this.form?.removeEventListener('submit', this.onSubmit);
        this.input?.removeEventListener('input', this.onInput);
        this.controller?.abort();
      }

      get sectionId() {
        return this.getAttribute('data-section-id');
      }

      // Codes currently applied (rendered chips). Codes Shopify rejected are not part of this list.
      get appliedCodes() {
        return Array.from(this.querySelectorAll('[data-remove]')).map((button) => button.getAttribute('data-remove'));
      }

      onClick(event) {
        const remove = event.target.closest('[data-remove]');
        if (remove) {
          const code = remove.getAttribute('data-remove');
          remove.disabled = true;
          this.update(this.appliedCodes.filter((applied) => applied !== code), true).catch(() => {
            remove.disabled = false;
          });
        }
      }

      onInput() {
        this.apply.disabled = this.input.value.trim() === '';
        this.setError('');
      }

      onSubmit(event) {
        event.preventDefault();

        const code = this.input.value.trim();
        if (code === '' || this.apply.getAttribute('aria-busy') === 'true') return;

        const existing = this.appliedCodes;
        if (existing.some((applied) => applied.toUpperCase() === code.toUpperCase())) return;

        this.setError('');
        this.apply.setAttribute('aria-busy', 'true');

        this.update([...existing, code], false)
          .then((cart) => {
            const result = cart.discount_codes?.find((discount) => discount.code.toUpperCase() === code.toUpperCase());
            if (!result || result.applicable === false) {
              // Wrike 4592855632: only Weekly Deal items in the cart -> "deal price already reduced, no code needed",
              // field emptied so "Einlösen" does not invite a second try. Round 4 (Ali 29.10.): no extra checkout button,
              // the drawer's own checkout button is the only one.
              const dealMessage = this.getAttribute('data-error-deal');
              if (dealMessage) {
                this.input.value = '';
                this.apply.disabled = true;
                this.setError(dealMessage, { deal: true });
              } else {
                this.setError(this.getAttribute('data-error'));
              }
              // Do not keep a rejected code on the cart (it would show up again as an error in checkout).
              return this.update(existing, false);
            }

            theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'cart-discount', cart });
          })
          .catch((error) => {
            if (error.name !== 'AbortError') this.setError(this.getAttribute('data-error-network'));
          })
          .finally(() => {
            this.apply?.removeAttribute('aria-busy');
          });
      }

      update(codes, publish) {
        this.controller?.abort();
        this.controller = new AbortController();

        // ST 2026-10-30 (Wrike 4595016691): sections_url renders the drawer in the page's language (also without a Referer).
        const body = JSON.stringify({ discount: codes.join(','), sections: [this.sectionId], sections_url: window.location.pathname });
        return fetch(theme.routes.cart_update_url, { ...theme.utils.fetchConfig('json'), body, signal: this.controller.signal })
          .then((response) => {
            if (!response.ok) throw new Error(`cart update ${response.status}`);
            return response.json();
          })
          .then((cart) => {
            if (publish) theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'cart-discount', cart });
            return cart;
          });
      }

      setError(message, { deal = false } = {}) {
        if (!this.error) return;
        this.error.lastElementChild.textContent = message;
        this.error.hidden = message === '';
        this.classList.toggle('is-error', message !== '' && !deal);
        this.classList.toggle('is-deal', message !== '' && deal);
        this.input?.setAttribute('aria-invalid', String(message !== '' && !deal));
        if (message !== '') this.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }
  );
}
